import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/axiosConfig';
import EmailCampaignModal from '../EmailCampaignModal';
import '../../styles/Campaigns.css';

function Campaigns() {
  const [pushNotification, setPushNotification] = useState({ title: '', body: '' });
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailsSent, setEmailsSent] = useState(0);
  const [emailLimit, setEmailLimit] = useState(3000);
  const [periodStart, setPeriodStart] = useState(null);
  const [limitRequest, setLimitRequest] = useState(null);
  const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);
  const [requestedLimit, setRequestedLimit] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [emailCredits, setEmailCredits] = useState(0);
  const [emailAllowance, setEmailAllowance] = useState(3000);
  const [topupTiers, setTopupTiers] = useState([]);
  const [isTopupOpen, setIsTopupOpen] = useState(false);
  const [buyingTierId, setBuyingTierId] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSendResult, setEmailSendResult] = useState(null);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchEmailStats();
    fetchTopupTiers();

    // PayFast sends the vendor back here after payment. The credit itself is
    // applied by the ITN, which may land a moment later, so just refresh.
    const params = new URLSearchParams(window.location.search);
    if (params.get('topup') === 'success') {
      setToast({
        type: 'success',
        message: 'Payment received. Your extra emails will appear shortly.'
      });
      setTimeout(fetchEmailStats, 4000);
    } else if (params.get('topup') === 'cancelled') {
      setToast({ type: 'error', message: 'Payment cancelled. Nothing was charged.' });
    }
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchEmailStats = async () => {
    try {
      const response = await axios.get('/api/vendors/email-stats');
      setEmailsSent(response.data.emailsSent);
      setEmailLimit(response.data.emailLimit);
      setPeriodStart(response.data.periodStart || null);
      setLimitRequest(response.data.limitRequest || null);
      setEmailCredits(response.data.emailCredits || 0);
      setEmailAllowance(response.data.emailAllowance || response.data.emailLimit);
    } catch (error) {
      console.error('Error fetching email stats:', error);
      setToast({
        type: 'error',
        message: 'Failed to load email statistics'
      });
    }
  };

  const handleEmailCampaignSubmit = async (emailContent, subject) => {
    setIsEmailModalOpen(false);
    setIsSendingEmail(true);
    try {
      const response = await axios.post('/api/vendors/send-campaign-email', {
        subject: subject,
        templateString: emailContent,
        data: {}
      });
      setEmailsSent(response.data.totalEmailsSent);
      setToast({
        type: 'success',
        message: `Campaign sent successfully to ${response.data.successCount} recipients`
      });
      fetchEmailStats();
    } catch (error) {
      console.error('Error sending campaign email:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.error || 'Failed to send campaign email'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handlePushNotificationSubmit = async (e) => {
    e.preventDefault();
    setNotificationStatus(null);
    setIsSendingNotification(true);
    try {
      const response = await axios.post('/api/vendors/send-push-notification', {
        title: pushNotification.title,
        body: pushNotification.body
      });
      
      setToast({
        type: 'success',
        message: `Push notifications sent to ${response.data.successCount} recipients`
      });
      setPushNotification({ title: '', body: '' });
    } catch (error) {
      console.error('Error sending push notifications:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.error || 'Failed to send push notifications'
      });
    } finally {
      setIsSendingNotification(false);
    }
  };

  const fetchTopupTiers = async () => {
    try {
      const response = await axios.get('/api/subscription/email-topup/tiers');
      setTopupTiers(response.data);
    } catch (error) {
      console.error('Error fetching top-up tiers:', error);
    }
  };

  const handleBuyTopup = async (tier) => {
    setBuyingTierId(tier.id);
    try {
      const response = await axios.post('/api/subscription/email-topup/create', {
        tierId: tier.id
      });
      // Hand off to PayFast.
      window.location.href = response.data.paymentUrl;
    } catch (error) {
      console.error('Error starting top-up payment:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.error || 'Could not start payment'
      });
      setBuyingTierId(null);
    }
  };

  const formatRands = (cents) =>
    `R${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleRequestIncrease = async (e) => {
    e.preventDefault();
    setIsSubmittingRequest(true);
    try {
      const response = await axios.post('/api/vendors/email-limit-request', {
        requestedLimit: requestedLimit === '' ? null : requestedLimit,
        message: requestMessage
      });
      setLimitRequest(response.data.request);
      setIsRequestFormOpen(false);
      setRequestedLimit('');
      setRequestMessage('');
      setToast({
        type: 'success',
        message: 'Request submitted. We will be in touch shortly.'
      });
    } catch (error) {
      console.error('Error requesting limit increase:', error);
      if (error.response?.status === 409 && error.response.data?.request) {
        setLimitRequest(error.response.data.request);
        setIsRequestFormOpen(false);
      }
      setToast({
        type: 'error',
        message: error.response?.data?.error || 'Failed to submit request'
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // periodStart is a plain YYYY-MM-DD date. Build the label from its parts so
  // the month does not shift for viewers outside the server's timezone.
  const periodLabel = periodStart
    ? new Date(
        Number(periodStart.slice(0, 4)),
        Number(periodStart.slice(5, 7)) - 1,
        1
      ).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : 'this month';

  const hasPendingRequest = limitRequest && limitRequest.status === 'pending';
  // Offered before they are fully blocked, so a busy month can be pre-empted.
  const canRequestIncrease = emailsSent >= emailAllowance * 0.8;

  const renderProgressBar = (current, max) => {
    const percentage = Math.min((current / max) * 100, 100);
    let color = '#4361ee'; // Primary color
    
    if (percentage > 90) {
      color = '#dc3545'; // Danger color
    } else if (percentage > 75) {
      color = '#ffc107'; // Warning color
    }
    
    return (
      <div className="cm-progress-container">
        <div 
          className="cm-progress-bar"
          style={{ 
            width: `${percentage}%`,
            backgroundColor: color
          }}
        ></div>
        <span className="cm-progress-text">
          {current} / {max} emails
        </span>
      </div>
    );
  };

  return (
    <div className="cm-container">
      {toast && (
        <div className={`cm-toast ${toast.type}`}>
          <div className="cm-toast-content">
            <span className={`cm-toast-icon ${toast.type}`}>
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="cm-toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="cm-header">
        <div className="cm-header-content">
          <h1>Campaigns</h1>
          <p className="cm-header-subtitle">Send email campaigns and push notifications to your customers</p>
        </div>
      </div>

      <div className="cm-grid">
        <div className="cm-card">
          <div className="cm-card-header">
            <h2>Email Campaigns</h2>
          </div>
          <div className="cm-card-body">
            <div className="cm-card-section">
              <h3>Email Usage</h3>
              <p className="cm-text">
                Your email usage for {periodLabel}. The count resets on the 1st of each month.
              </p>

              {renderProgressBar(emailsSent, emailAllowance)}

              {emailCredits > 0 && (
                <p className="cm-text">
                  Includes {emailCredits} top-up emails on top of your {emailLimit}
                  {' '}monthly allowance. Top-up emails do not carry over.
                </p>
              )}

              <div className="cm-card-actions">
                <button
                  onClick={() => setIsEmailModalOpen(true)}
                  className="cm-button primary"
                  disabled={isSendingEmail || emailsSent >= emailAllowance}
                >
                  {isSendingEmail ? (
                    <>
                      <span className="cm-spinner"></span> Sending...
                    </>
                  ) : (
                    'Create Email Campaign'
                  )}
                </button>

                {canRequestIncrease && !hasPendingRequest && !isRequestFormOpen && (
                  <button
                    onClick={() => setIsRequestFormOpen(true)}
                    className="cm-button secondary"
                  >
                    Request Limit Increase
                  </button>
                )}

                {canRequestIncrease && topupTiers.length > 0 && !isTopupOpen && (
                  <button
                    onClick={() => setIsTopupOpen(true)}
                    className="cm-button primary"
                  >
                    Buy Extra Emails
                  </button>
                )}
              </div>

              {isTopupOpen && (
                <div className="cm-card-section">
                  <h3>Buy extra emails for this month</h3>
                  <p className="cm-text">
                    Pay once and send straight away. Extra emails apply to
                    {' '}{periodLabel} only and do not carry over.
                  </p>
                  <div className="cm-topup-tiers">
                    {topupTiers.map(tier => (
                      <div key={tier.id} className="cm-topup-tier">
                        <div className="cm-topup-tier-emails">
                          {tier.emails.toLocaleString('en-ZA')} emails
                        </div>
                        <div className="cm-topup-tier-price">{formatRands(tier.price_cents)}</div>
                        <div className="cm-topup-tier-unit">
                          {formatRands(tier.price_cents / tier.emails)} each
                        </div>
                        <button
                          className="cm-button primary"
                          onClick={() => handleBuyTopup(tier)}
                          disabled={buyingTierId !== null}
                        >
                          {buyingTierId === tier.id ? (
                            <>
                              <span className="cm-spinner"></span> Redirecting...
                            </>
                          ) : (
                            'Buy'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="cm-form-actions">
                    <button
                      type="button"
                      className="cm-button secondary"
                      onClick={() => setIsTopupOpen(false)}
                      disabled={buyingTierId !== null}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {hasPendingRequest && (
                <div className="cm-info-panel">
                  <span className="cm-info-icon">&#8987;</span>
                  <div className="cm-info-content">
                    <p className="cm-text">
                      Your request to increase this limit is with us and awaiting
                      review. We will email you as soon as it is decided.
                    </p>
                  </div>
                </div>
              )}

              {isRequestFormOpen && (
                <form className="cm-form" onSubmit={handleRequestIncrease}>
                  <div className="cm-form-group">
                    <label className="cm-label" htmlFor="requestedLimit">
                      Emails you need per month
                    </label>
                    <input
                      id="requestedLimit"
                      type="number"
                      min={emailLimit + 1}
                      className="cm-input"
                      value={requestedLimit}
                      onChange={(e) => setRequestedLimit(e.target.value)}
                      placeholder={`More than ${emailLimit}`}
                    />
                  </div>
                  <div className="cm-form-group">
                    <label className="cm-label" htmlFor="requestMessage">
                      Anything we should know? (optional)
                    </label>
                    <textarea
                      id="requestMessage"
                      className="cm-textarea"
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="cm-form-actions">
                    <button type="submit" className="cm-button primary" disabled={isSubmittingRequest}>
                      {isSubmittingRequest ? (
                        <>
                          <span className="cm-spinner"></span> Sending...
                        </>
                      ) : (
                        'Send Request'
                      )}
                    </button>
                    <button
                      type="button"
                      className="cm-button secondary"
                      onClick={() => setIsRequestFormOpen(false)}
                      disabled={isSubmittingRequest}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              
              <EmailCampaignModal
                isOpen={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                onSubmit={handleEmailCampaignSubmit}
                isSending={isSendingEmail}
              />
            </div>
          </div>
        </div>

        <div className="cm-card">
          <div className="cm-card-header">
            <h2>Push Notifications</h2>
          </div>
          <div className="cm-card-body">
            <form onSubmit={handlePushNotificationSubmit} className="cm-form">
              <div className="cm-form-group">
                <label className="cm-label" htmlFor="notification-title">
                  Notification Title <span className="cm-required">*</span>
                </label>
                <input
                  id="notification-title"
                  type="text"
                  className="cm-input"
                  placeholder="e.g., Special Offer"
                  value={pushNotification.title}
                  onChange={(e) => setPushNotification({...pushNotification, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="cm-form-group">
                <label className="cm-label" htmlFor="notification-body">
                  Notification Message <span className="cm-required">*</span>
                </label>
                <textarea
                  id="notification-body"
                  className="cm-input cm-textarea"
                  placeholder="e.g., Visit us today for a special discount!"
                  value={pushNotification.body}
                  onChange={(e) => setPushNotification({...pushNotification, body: e.target.value})}
                  required
                  rows="4"
                ></textarea>
              </div>
              
              <div className="cm-form-actions">
                <button 
                  type="submit" 
                  className="cm-button primary"
                  disabled={isSendingNotification}
                >
                  {isSendingNotification ? (
                    <>
                      <span className="cm-spinner"></span> Sending...
                    </>
                  ) : (
                    'Send Push Notification'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="cm-info-panel">
        <div className="cm-info-icon">ℹ️</div>
        <div className="cm-info-content">
          <h3>Campaign Best Practices</h3>
          <ul>
            <li>Keep your subject lines short and engaging</li>
            <li>Include a clear call-to-action in your campaigns</li>
            <li>Optimize for mobile viewing</li>
            <li>Test your emails before sending to all customers</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Campaigns;